'use client';

import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../../config/theme';

const FormContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  background: transparent;
`;

const FormHeader = styled.div`
  margin-bottom: 40px;
`;

const FormTitle = styled.h1`
  font-size: 2rem;
  font-weight: ${theme.fontWeights.bold};
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const FormSubtitle = styled.p`
  font-size: 1rem;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
`;

const SectionTitle = styled.h2`
  font-size: 0.75rem;
  font-weight: ${theme.fontWeights.semibold};
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 24px 0;
`;

const ChannelList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const ChannelItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  border-bottom: 1px solid #e5e7eb;
  background: transparent;

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ChannelLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ChannelName = styled.div`
  font-size: 1rem;
  font-weight: ${theme.fontWeights.medium};
  color: #1f2937;
`;

const ChannelInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 500px;
  width: 500px;
`;

const ChannelType = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: ${theme.fontWeights.medium};
`;

const ChannelValue = styled.input`
  font-size: 0.875rem;
  color: #1f2937;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 10px 14px;
  background: #ffffff;
  transition: all 0.2s ease;
  width: 100%;
  max-width: 500px;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleSwitch = styled.div<{ enabled: boolean }>`
  position: relative;
  width: 52px;
  height: 28px;
  background: ${({ enabled }) => enabled ? '#10b981' : '#d1d5db'};
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ enabled }) => enabled ? '#059669' : '#9ca3af'};
  }
`;

const ToggleCircle = styled.div<{ enabled: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ enabled }) => enabled ? '26px' : '2px'};
  width: 24px;
  height: 24px;
  background: #ffffff;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;


export interface NotificationChannel {
  id: string;
  name: string;
  type: string;
  value: string;
  enabled: boolean;
}

interface AlertsConfigurationProps {
  onSave?: (channels: NotificationChannel[]) => void;
  onCancel?: () => void;
}

export const AlertsConfiguration: React.FC<AlertsConfigurationProps> = () => {
  const [channels, setChannels] = useState<NotificationChannel[]>([
    {
      id: 'integration-email',
      name: 'Teams',
      type: 'Email',
      value: '',
      enabled: true
    },
    {
      id: 'devops-email',
      name: 'DevOps Team Email',
      type: 'Email',
      value: '',
      enabled: true
    },
    {
      id: 'ocapi-slack',
      name: 'Slack',
      type: 'Slack Channel',
      value: '',
      enabled: true
    },
    {
      id: 'webdav-teams',
      name: 'Webhook',
      type: 'Webhook',
      value: '',
      enabled: true
    }
  ]);

  const toggleChannel = (id: string) => {
    setChannels(prev => prev.map(channel => 
      channel.id === id ? { ...channel, enabled: !channel.enabled } : channel
    ));
  };

  const updateChannelValue = (id: string, value: string) => {
    setChannels(prev => prev.map(channel => 
      channel.id === id ? { ...channel, value } : channel
    ));
  };

  return (
    <FormContainer>
      <FormHeader>
        <FormTitle>Alerts Configuration</FormTitle>
        <FormSubtitle>Enhanced Notification Channels</FormSubtitle>
      </FormHeader>

      <SectionTitle>Channels</SectionTitle>
      
      <ChannelList>
        {channels.map((channel) => (
          <ChannelItem key={channel.id}>
            <ChannelLeft>
              <ChannelName>{channel.name}</ChannelName>
              <ToggleContainer>
                <ToggleSwitch 
                  enabled={channel.enabled}
                  onClick={() => toggleChannel(channel.id)}
                >
                  <ToggleCircle enabled={channel.enabled} />
                </ToggleSwitch>
              </ToggleContainer>
            </ChannelLeft>
            
            <ChannelInfo>
              <ChannelType>{channel.type}</ChannelType>
              <ChannelValue
                type="text"
                value={channel.value}
                onChange={(e) => updateChannelValue(channel.id, e.target.value)}
                disabled={!channel.enabled}
                placeholder={`Enter ${channel.type.toLowerCase()}`}
              />
            </ChannelInfo>
          </ChannelItem>
        ))}
      </ChannelList>

    </FormContainer>
  );
};

export default AlertsConfiguration;
